import {memo, useCallback, useEffect, useState} from 'react';

import {view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {
  isMessageInteractivityEnabled,
  useConversationExceptions,
  useConversationInlineDataEvents,
} from '@stripe-internal/conversations-assistant';

import {MobileConversationInput} from './MobileConversationInput';
import {MobileLog} from './MobileLog';

type Props = {
  conversation: Conversation;
};

export const MobileConversationScreen = memo(function ConversationScreen({
  conversation,
}: Props) {
  const [disableMessageInteractivity, setDisableMessageInteractivity] =
    useState<boolean>(!isMessageInteractivityEnabled(conversation));

  const bindToConversationState = useCallback(() => {
    setDisableMessageInteractivity(
      !isMessageInteractivityEnabled(conversation),
    );
  }, [conversation]);

  useEffect(() => {
    bindToConversationState();
  }, [bindToConversationState]);

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      bindToConversationState();
    });

    return () => sub.unsubscribe();
  }, [conversation, bindToConversationState]);

  useConversationExceptions(conversation);
  useConversationInlineDataEvents(conversation);

  return (
    <view.div
      data-testid="conversation-screen"
      css={{
        stack: 'y',
        height: 'fill',
        position: 'relative',
      }}
      // Setting the `aria-live` attribute so that screen readers can read new messages coming in.
      // We set it to `polite` so that new message don't interrupt the reading of previous messages.
      aria-live="polite"
    >
      <view.div css={{flex: '1 1 auto', minHeight: 0, overflow: 'hidden'}}>
        <MobileLog
          conversation={conversation}
          animated
          disableInteraction={disableMessageInteractivity}
        />
      </view.div>
      <view.div css={{flex: '0 0 auto'}}>
        <MobileConversationInput conversation={conversation} />
      </view.div>
    </view.div>
  );
});
